<template>
    <b-modal :id="modalId" hide-footer size="xl" :title="modalTitle" v-model="modalVisible"
        @hide="resetTransfer">
        <template v-if="store.disallowTransferOut">
            <b-alert v-model="store.disallowTransferOut" variant="danger">
                <i class="fa fa-warning" />
                <strong>
                    This store disallows transferring out.
                </strong>
            </b-alert>
            <b-button block class="mt-3" @click="cancelTransfer" variant="primary">
                Close
            </b-button>
        </template>
        <template v-else-if="hasActiveClaims && !acceptAndTransferMode">
            <b-alert show variant="danger">
                <strong>
                    <i class="fas fa-exclamation-triangle"></i>
                    One or more prescriptions have an active claim (paid, rejected, or price schedule) and cannot be transferred.
                </strong>
            </b-alert>
            <b-button block class="mt-3" @click="cancelTransfer" variant="primary">
                Close
            </b-button>
        </template>
        <template v-else>
            <!-- Accept and Transfer Info Banner -->
            <b-alert v-if="acceptAndTransferMode" show variant="info" class="mb-3">
                <i class="fas fa-info-circle"></i>
                <strong>Accept and Transfer</strong> will:
                <ol class="mb-0 mt-2">
                    <li>Auto-reverse any active (paid) claims</li>
                    <li>Complete PV1 verification</li>
                    <li>Transfer the prescription to the selected pharmacy</li>
                </ol>
            </b-alert>

            <!-- Active Claims Warning for Accept and Transfer -->
            <b-alert v-if="acceptAndTransferMode && activeClaims.length > 0" show variant="warning" class="mb-3">
                <i class="fas fa-exclamation-triangle"></i>
                <strong>{{ activeClaims.length }} active claim(s)</strong> will be reversed before transfer.
            </b-alert>

            <b-row>
                <b-col>
                    <b-form-group label="Transfer Type*">
                        <b-form-radio v-model="transferData.verbalTransfer" :value="true" inline>
                            Verbal
                        </b-form-radio>
                        <b-form-radio v-model="transferData.verbalTransfer" :value="false" inline>
                            Electronic / Fax
                        </b-form-radio>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b-badge v-if="pharmacySupportsRxTransfer && !disableSurescriptsETransfer" variant="primary" class="pull-right">
                        <i class="fa fa-bolt"></i>
                        Supports Electronic RxTransfer
                    </b-badge>
                    <strong>
                        <SearchComponent :disabled="!!pharmacyDetail.id" v-model="pharmacyDetail"
                            search-u-r-l="/StoredPharmacy" :params="{ showOnlyActive: true }" create-new-object
                            label="Select Pharmacy*" createNewLabel="Create New Pharmacy"
                            :id="pharmacyDetail && pharmacyDetail.id" @input="onPharmacyChange"
                            @createNewObject="newStoredPharma" />
                    </strong>
                </b-col>
            </b-row>

            <b-row v-if="pharmacyDetail && pharmacyDetail.id">
                <b-col :class="{ 'font-weight-bolder': isPharmacistRequired }">
                    <SearchComponent :disabled="!pharmacyDetail || !pharmacyDetail.id" v-model="pharmacistDetail"
                        :search-u-r-l="`/StoredPharmacist/GetAllPharmacistsByStore/${pharmacyDetail.id}`"
                        :params="{ showOnlyActive: true }" create-new-object :label="pharmacistLabelText"
                        createNewLabel="Create New Pharmacist" :id="pharmacistDetail && pharmacistDetail.id"
                        @createNewObject="newStoredPharmacist" prepopulate />
                </b-col>
            </b-row>

            <b-row>
                <b-col :class="{ 'font-weight-bolder': isTransferReasonRequired }">
                    <SearchComponent v-model="transferReason" :id="transferReason && transferReason.id"
                        search-u-r-l="/RxTransfer/Reasons" :label="transferReasonLabel" prepopulate
                        @input="onTransferReasonChange" />
                </b-col>
            </b-row>

            <b-row v-if="shouldShowInsurancePlans">
                <b-col>
                    <h6>Plans to Include</h6>
                    <b-table id="patient-insurance-table" :items="combinedPlans" show-empty empty-text="No Plans Found">
                        <template #cell(patientPlan)="row">
                            <div v-html="row.item.patientPlan.toString()"></div>
                        </template>
                        <template #cell(include)="row">
                            <b-checkbox v-model="row.item.include" />
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <template v-if="transferData.verbalTransfer != null && !transferData.verbalTransfer">
                <b-row>
                    <b-col>
                        <b-form-group label="Note on Fax:">
                            <b-textarea v-model="transferData.note" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="shouldShowPrescriptionImageOption">
                    <b-col>
                        <b-form-group>
                            <b-form-checkbox v-model="transferData.appendPrescriptionImage">
                                Include Prescription Image
                                <HelpPopup>
                                    When enabled, the original prescription image will be included with the transfer fax
                                </HelpPopup>
                            </b-form-checkbox>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="shouldShowPatientDocuments">
                    <b-col>
                        <b-table show-empty empty-text="No documents found associated with Patient"
                            :api-url="`/patient/${singlePatientId}/get-documents/`" :items="patientDocProvider"
                            :fields="patientDocFields">
                            <template #cell(fileName)="data">
                                <strong>
                                    <a href="" @click.prevent="openDocument(data.item)">{{ data.value }}</a>
                                </strong>
                            </template>
                            <template #cell(select)="data">
                                <b-checkbox v-model="data.value" @change="updateDocuments(data.item.id, $event)">
                                </b-checkbox>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-form-group>
                            <label>
                                Additional Documents to Include (PDF)
                                <HelpPopup>
                                    Additional supporting documents that will be faxed in addition to the standard
                                    transfer document.
                                </HelpPopup>
                            </label>
                            <b-form-file id="uploadFile" v-model="additionalDocuments" accept=".pdf"
                                drop-placeholder="Drop file here..."
                                placeholder="Choose additional document or drop it here..." multiple>
                            </b-form-file>
                        </b-form-group>
                    </b-col>
                </b-row>
            </template>

            <b-row v-if="isTransferring" class="justify-content-center align-items-center flex-column">
                <b-spinner class="mr-2" label="Spinning" />
                <strong class="mt-2">{{ processingStatus }}</strong>
            </b-row>
            <b-row v-else>
                <b-col>
                    <b-button block class="mt-3" @click="cancelTransfer" variant="danger">
                        Cancel
                    </b-button>
                </b-col>
                <b-col>
                    <b-button :disabled="isTransferDisabled" block class="mt-3" variant="success"
                        @click="performTransfer">
                        {{ acceptAndTransferMode ? 'Accept & Transfer' : 'Transfer' }}
                    </b-button>
                </b-col>
            </b-row>
        </template>

        <!-- Child Modals -->
        <b-modal id="addNewPharmacy" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacy">
            <PharmacyDetailPage new-mode @close="$bvModal.hide('addNewPharmacy')" @created="onPharmacyCreate"
                :default-to-active="true" :hide-title="true" />
        </b-modal>

        <b-modal id="addNewPharmacist" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacist">
            <PharmacistDetailPage :disabled="!pharmacyDetail || !pharmacyDetail.id"
                :pharmacy-id="pharmacyDetail && +pharmacyDetail.id" new-mode @close="$bvModal.hide('addNewPharmacist')"
                @created="onPharmacistCreate" :default-to-active="true" :hide-title="true" />
        </b-modal>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Axios, { AxiosError } from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import PharmacyDetailPage from '@/pages/Pharmacy/PharmacyDetailPage.vue';
import PharmacistDetailPage from '@/pages/Pharmacist/PharmacistDetailPage.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { PharmacyRecord } from '@/models/Pharmacy';
import { PharmacistRecord } from '@/models/Pharmacist';
import { Store } from '@/models/Store';
import { Patient } from '@/models/Patient';
import { Prescription, FaxForm } from '@/models/Prescription';
import { Plan } from '@/models/Plan';
import { PatientPlan } from '@/models/PatientPlan';
import { IncludeCombinedPlan } from '@/models/CombinedPlan';
import { PatientDocument } from '@/models/PatientDocument';
import { BvTableCtxObject } from 'bootstrap-vue';
import {Configuration} from '@/models/Configuration';
import { Claim } from '@/models/Claim';
import { RxTransferReason, ExternalTransferPrefill } from '@/models/RxTransfer';

@Component({
    name: 'ExternalTransferModal',
    components: {
        SearchComponent,
        PharmacyDetailPage,
        PharmacistDetailPage,
        HelpPopup
    }
})
export default class ExternalTransferModal extends Vue {
    @Prop({ required: true }) prescriptions!: Array<Prescription>;
    @Prop({ required: true }) store!: Store;
    @Prop() patient?: Patient;
    @Prop({ default: false }) acceptAndTransferMode!: boolean;
    @Prop() prefill?: ExternalTransferPrefill;
    @Prop() modalIdOverride?: string;

    // Transfer data
    private transferData: FaxForm = {
        appendPrescriptionImage: true
    } as FaxForm;
    private pharmacyDetail: PharmacyRecord = new PharmacyRecord();
    private pharmacistDetail: PharmacistRecord | null = new PharmacistRecord();
    private transferReason: RxTransferReason = new RxTransferReason();
    private additionalDocuments: File[] = [];
    private selectedDocuments: number[] = [];
    private combinedPlans: IncludeCombinedPlan[] = [];
    private pharmacySupportsRxTransfer: boolean | null = null;
    private pharmacySupportsTMRx: boolean | null = null;
    private requirePharmacistConfig: boolean = false;
    private requireTransferReasonConfig: boolean = false;
    private includePrescriptionImageConfig: boolean = true;
    private disableSurescriptsETransfer: boolean = false;

    // State flags
    private isTransferring: boolean = false;
    private modalVisible: boolean = false;
    private processingStatus: string = 'Transferring...';

    get modalId(): string {
        if (this.modalIdOverride) return this.modalIdOverride;
        return this.acceptAndTransferMode ? 'accept-and-transfer-modal' : 'external-transfer-modal';
    }

    get modalTitle(): string {
        return this.acceptAndTransferMode ? 'Accept and Transfer' : 'External Transfer';
    }

    get effectivePrescriptions(): Array<Prescription> {
        return this.prescriptions;
    }

    get singlePatientId(): number | undefined {
        const rxList = this.effectivePrescriptions;
        if (!rxList?.length) return undefined;
        const firstPatientId = rxList[0].patientID;
        const allSamePatient = rxList.every(rx => rx.patientID === firstPatientId);
        return allSamePatient ? firstPatientId : undefined;
    }

    get hasActiveClaims(): boolean {
        // Any non-reversed claim (paid, rejected, or price-schedule) blocks the transfer; server is authoritative.
        return this.effectivePrescriptions.some(rx => rx.claims?.some(claim => !claim.reversed));
    }

    get activeClaims(): Claim[] {
        if (!this.acceptAndTransferMode || this.effectivePrescriptions.length === 0) return [];
        return this.effectivePrescriptions[0].claims?.filter(c => !c.reversed && c.transactionStatus === 'Paid') ?? [];
    }

    get shouldShowInsurancePlans(): boolean {
        return !!this.singlePatientId &&
               this.combinedPlans.length > 0 &&
               this.transferData.verbalTransfer === false;
    }

    get shouldShowPatientDocuments(): boolean {
        return !!this.singlePatientId &&
               this.transferData.verbalTransfer === false;
    }

    get shouldShowPrescriptionImageOption(): boolean {
        return this.includePrescriptionImageConfig &&
               this.transferData.verbalTransfer === false &&
               this.effectivePrescriptions.some(rx => rx.imageID != null || rx.escriptID != null);
    }

    get isPharmacistRequired(): boolean {
        // If configuration requires pharmacist, always required
        if (this.requirePharmacistConfig) {
            return true;
        }

        // For verbal transfers when config is false, pharmacist is optional
        if (this.transferData.verbalTransfer === true) {
            return false;
        }

        // For electronic/fax transfers: check if pharmacy supports RxTransfer
        if (!this.transferData.verbalTransfer && (this.pharmacySupportsRxTransfer || this.pharmacySupportsTMRx)) {
            return false;
        }

        // For electronic/fax transfers without confirmed RxTransfer/TMRx support, pharmacist is required
        if (this.transferData.verbalTransfer === false && this.pharmacySupportsRxTransfer !== true && !this.pharmacySupportsTMRx) {
            return true;
        }

        // Default to not required (should not normally reach here)
        return false;
    }

    get pharmacistLabelText(): string {
        return this.isPharmacistRequired
            ? "Select Pharmacist*"
            : "Select Pharmacist (Optional)";
    }

    get isTransferReasonRequired(): boolean {
        // External transfers are always outbound, so the toggle alone governs the requirement.
        return this.requireTransferReasonConfig;
    }

    get transferReasonLabel(): string {
        return this.isTransferReasonRequired ? "Transfer Reason*" : "Transfer Reason (Optional)";
    }

    get isTransferDisabled(): boolean {
        // Transfer type must be selected
        if (this.transferData.verbalTransfer == null) return true;

        // Pharmacy is always required for both verbal and electronic/fax
        if (!this.pharmacyDetail.id) return true;

        // If pharmacist is required but not selected, disable the button
        if (this.isPharmacistRequired && !this.pharmacistDetail?.id) {
            return true;
        }

        // If transfer reason is required but not selected, disable the button
        if (this.isTransferReasonRequired && !this.transferReason?.id) {
            return true;
        }

        // All required fields are filled
        return false;
    }

    onTransferReasonChange() {
        this.transferData.rxTransferReasonId = this.transferReason?.id ?? null;
    }

    get patientDocFields() {
        return [
            "fileName",
            "select",
        ];
    }

    @Watch('singlePatientId')
    async loadInsurancePlans() {
        if (!this.singlePatientId) {
            this.combinedPlans = [];
            return;
        }

        try {
            type PlanResponse = { plans: Plan[]; patientPlans: PatientPlan[]; };
            const response = await Axios.get<PlanResponse>(`/api/Insurance/${this.singlePatientId}`);
            this.combinedPlans = response.data.patientPlans
                .filter(pp => pp.active)
                .map(pp => new IncludeCombinedPlan({
                    patientPlan: new PatientPlan(pp),
                    plan: new Plan(response.data.plans.find(p => p.id == pp.planId)),
                    include: false,
                } as IncludeCombinedPlan));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.combinedPlans = [];
        }
    }

    @Watch('modalVisible')
    async onModalVisibilityChange(isVisible: boolean) {
        if (isVisible && this.singlePatientId) {
            await this.loadInsurancePlans();
        }
        if (isVisible && this.acceptAndTransferMode) {
            this.prepopulateTransferPharmacy();
        }
        if (isVisible && this.prefill) {
            this.applyPrefill();
        }
    }

    private prepopulateTransferPharmacy() {
        const transferPharmacies = this.prescriptions[0]?.program?.transferToPharmacies;
        if (!transferPharmacies?.length) return;

        this.pharmacyDetail = new PharmacyRecord(transferPharmacies[0]);
    }

    private applyPrefill() {
        if (!this.prefill?.pharmacyId) return;

        const pharmacy = new PharmacyRecord();
        pharmacy.id = this.prefill.pharmacyId;
        this.pharmacyDetail = pharmacy;
        this.onPharmacyChange(pharmacy);
    }

    @Watch('includePrescriptionImageConfig')
    onIncludePrescriptionImageConfigChange(newValue: boolean) {
        // If configuration is disabled, force appendPrescriptionImage to false
        if (!newValue) {
            this.transferData.appendPrescriptionImage = false;
        }
    }

    @Watch("pharmacyDetail")
    async handlePharmacyChange(newValue: PharmacyRecord, oldValue: PharmacyRecord) {
        try {
            if (!newValue.id || newValue.id === oldValue.id) return;
            this.pharmacySupportsRxTransfer = null;
            this.pharmacySupportsTMRx = newValue.supportsTransferMyRx;
            const resp = await Axios.get(`/RxTransfer/TransferDirectoryLookup/${this.pharmacyDetail.id}`);
            this.pharmacySupportsRxTransfer = true;
            this.pharmacyDetail = resp.data;
        } catch {
            this.pharmacySupportsRxTransfer = false;
        }
    }

    onPharmacyChange(_value: PharmacyRecord) {
        if (this.pharmacyDetail.id) {
            Axios.get<PharmacyRecord>(`/StoredPharmacy/${this.pharmacyDetail.id}`).then(
                (response) => {
                    this.pharmacyDetail = new PharmacyRecord(response.data);
                    this.transferData.name = this.pharmacyDetail.name;
                    this.transferData.address = this.pharmacyDetail.address;
                    this.transferData.city = this.pharmacyDetail.addressCity;
                    this.transferData.state = this.pharmacyDetail.addressState;
                    this.transferData.zip = this.pharmacyDetail.addressZip;
                    this.transferData.phone = this.pharmacyDetail.phoneNumber;
                    this.transferData.fax = this.pharmacyDetail.faxNumber;
                    this.transferData.dea = this.pharmacyDetail.dea;
                    this.transferData.pharmacist = this.pharmacistDetail?.lastName
                        ? `${this.pharmacistDetail.lastName}, ${this.pharmacistDetail.firstName}`
                        : this.pharmacyDetail.pharmacist;
                    this.transferData.storedPharmacyId = this.pharmacyDetail.id;
                },
            ).catch(err => this.$notification(NotificationOptions.error(err)));
        }
    }

    newStoredPharma() {
        this.pharmacyDetail = new PharmacyRecord();
        this.$bvModal.show("addNewPharmacy");
    }

    newStoredPharmacist() {
        this.pharmacistDetail = new PharmacistRecord();
        this.$bvModal.show("addNewPharmacist");
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.pharmacyDetail = newPharma;
        this.$bvModal.hide("addNewPharmacy");
    }

    onPharmacistCreate(newPharmacist: PharmacistRecord) {
        this.pharmacistDetail = newPharmacist;
        this.$bvModal.hide("addNewPharmacist");
    }

    async openDocument(pd: PatientDocument) {
        try {
            const res = await Axios.get(`image/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting image to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    updateDocuments(documentId: number, checked: boolean) {
        if (checked) {
            this.selectedDocuments.push(documentId);
        } else {
            this.selectedDocuments = this.selectedDocuments.filter((id) => id !== documentId);
        }
    }

    async patientDocProvider(ctx: BvTableCtxObject) {
        try {
            const response = await Axios.get<Array<PatientDocument>>(ctx.apiUrl!);
            return response.data.map((r) => new PatientDocument(r));
        } catch (err) {
            console.error("Error while getting patient documents", err);
            return [];
        }
    }

    async performTransfer() {
        if (this.isTransferring) return;
        try {
            // Validate that pharmacist is provided when required
            if (this.isPharmacistRequired && !this.pharmacistDetail?.id) {
                this.$notification(NotificationOptions.error('Pharmacist name is required for this transfer'));
                return;
            }

            // Validate that a transfer reason is provided when required
            if (this.isTransferReasonRequired && !this.transferReason?.id) {
                this.$notification(NotificationOptions.error('Transfer Reason is required for this transfer'));
                return;
            }

            this.isTransferring = true;
            this.processingStatus = this.acceptAndTransferMode
                ? 'Processing Accept and Transfer...'
                : 'Transferring...';

            if (this.pharmacistDetail?.lastName) {
                this.transferData.pharmacist = `${this.pharmacistDetail.lastName}, ${this.pharmacistDetail.firstName}`;
            }

            this.transferData.patientPlanIds = this.combinedPlans
                .filter(v => v.include)
                .map(p => +p.patientPlan.id);

            (this.transferData as any).prescriptionIds = this.effectivePrescriptions.map(rx => rx.rxID);
            (this.transferData as any).documentIds = this.selectedDocuments;

            const formData = new FormData();
            formData.append("record", JSON.stringify(this.transferData));

            this.additionalDocuments.forEach((file, index) => {
                formData.append(`addDocuments[${index}]`, file);
            });

            const postOptions = {
                headers: { 'Content-Type': 'multipart/form-data' },
            };

            let response;
            if (this.acceptAndTransferMode) {
                // Accept-and-transfer only works with a single prescription
                if (this.effectivePrescriptions.length !== 1) {
                    this.$notification(NotificationOptions.error('Accept and Transfer requires exactly one prescription'));
                    return;
                }
                const rx = this.effectivePrescriptions[0];
                const prescriptionId = `${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}`;

                response = await Axios.post(
                    `/Prescription/${prescriptionId}/accept-and-transfer`,
                    formData,
                    postOptions
                );

                // Handle accept-and-transfer response
                const result = response.data;
                let successMessage = `Prescription accepted and transferred via ${result.transferType || 'External'}`;

                // Show warnings if any reversals failed
                if (result.warnings && result.warnings.length > 0) {
                    this.$notification(NotificationOptions.warning(
                        `Transfer completed with warnings:\n${result.warnings.join('\n')}`
                    ));
                }

                this.$notification(NotificationOptions.success(successMessage));
            } else {
                response = await Axios.post(`/Prescription/external-transfer`, formData, postOptions);

                const prescriptionCount = this.effectivePrescriptions.length;
                let successMessage: string;

                if (prescriptionCount === 1) {
                    const transferType = response.data?.transferType || 'External';
                    successMessage = `Prescription transferred via ${transferType}`;
                } else {
                    successMessage = 'Prescriptions transferred';
                }

                this.$notification(NotificationOptions.success(successMessage));
            }

            this.$emit('transferred', response.data);
            this.cancelTransfer();
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error));
            this.$emit('error', error);
        } finally {
            this.isTransferring = false;
            this.processingStatus = 'Transferring...';
        }
    }

    cancelTransfer() {
        this.$bvModal.hide(this.modalId);
    }

    resetTransfer() {
        this.transferData = {
            appendPrescriptionImage: true
        } as FaxForm;
        this.pharmacyDetail = new PharmacyRecord();
        this.pharmacistDetail = new PharmacistRecord();
        this.transferReason = new RxTransferReason();
        this.additionalDocuments = [];
        this.selectedDocuments = [];
        this.pharmacySupportsRxTransfer = null;
        this.pharmacySupportsTMRx = null;
        this.isTransferring = false;
        this.modalVisible = false;
        this.$emit('closed');
    }

    async mounted() {
        this.loadInsurancePlans();
        await this.fetchConfiguration();
    }

    async fetchConfiguration() {
        try {
            const response = await Axios.get<Record<string, boolean>>(
                '/configuration/RequirePharmacistOnExternalTransfers,IncludePrescriptionImageOnExternalTransfers,DisableSurescriptsETransfer,RequireTransferReasonOnOutboundTransfers'
            );

            this.requirePharmacistConfig = response.data.RequirePharmacistOnExternalTransfers ?? false;
            this.includePrescriptionImageConfig = response.data.IncludePrescriptionImageOnExternalTransfers ?? true;
            this.disableSurescriptsETransfer = response.data.DisableSurescriptsETransfer ?? false;
            this.requireTransferReasonConfig = response.data.RequireTransferReasonOnOutboundTransfers ?? false;

            if (!this.includePrescriptionImageConfig) {
                this.transferData.appendPrescriptionImage = false;
            }
        } catch (err) {
            console.warn('Failed to fetch configuration:', err);
            this.requirePharmacistConfig = false;
            this.includePrescriptionImageConfig = true;
            this.disableSurescriptsETransfer = false;
            this.requireTransferReasonConfig = false;
        }
    }
}
</script>

<style scoped>
/* Add any specific styles if needed */
</style>
